/**
 * Lightweight Excel (.xlsx) export utility using HTML table + Blob.
 * Generates a valid SpreadsheetML file that Excel, Google Sheets, and
 * LibreOffice can all open with formatting intact.
 *
 * No external dependencies required.
 */

interface ExcelColumn {
  header: string
  key: string
  width?: number
  /** 'currency' applies ₹#,##0.00 format, 'number' right-aligns */
  format?: 'currency' | 'number' | 'text'
}

interface ExcelExportOptions {
  sheetName: string
  title: string
  subtitle?: string
  columns: ExcelColumn[]
  rows: Record<string, unknown>[]
  totalsRow?: Record<string, unknown>
  fileName: string
  freezeHeaderRow?: boolean
}

function escapeXml(val: unknown): string {
  const s = String(val ?? '')
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function exportToExcel(options: ExcelExportOptions): void {
  const { sheetName, title, subtitle, columns, rows, totalsRow, fileName, freezeHeaderRow } = options

  // Build SpreadsheetML XML
  let currentRow = 1

  // Column widths
  const colDefs = columns
    .map(
      (col, idx) =>
        `<Column ss:Index="${idx + 1}" ss:AutoFitWidth="1" ss:Width="${col.width ?? (col.format === 'currency' ? 120 : 100)}"/>`
    )
    .join('\n')

  // Title row
  const titleRow = `
    <Row ss:Height="24">
      <Cell ss:MergeAcross="${columns.length - 1}" ss:StyleID="title">
        <Data ss:Type="String">${escapeXml(title)}</Data>
      </Cell>
    </Row>`
  currentRow++

  // Subtitle row
  let subtitleRow = ''
  if (subtitle) {
    subtitleRow = `
    <Row ss:Height="18">
      <Cell ss:MergeAcross="${columns.length - 1}" ss:StyleID="subtitle">
        <Data ss:Type="String">${escapeXml(subtitle)}</Data>
      </Cell>
    </Row>`
    currentRow++
  }

  // Empty row
  const emptyRow = '<Row></Row>'
  currentRow++

  // Header row
  const headerCells = columns
    .map((col) => `<Cell ss:StyleID="header"><Data ss:Type="String">${escapeXml(col.header)}</Data></Cell>`)
    .join('')
  const headerRow = `<Row ss:Height="20">${headerCells}</Row>`
  const headerRowIdx = currentRow
  currentRow++

  // Data rows
  const dataRows = rows
    .map((row) => {
      const cells = columns
        .map((col) => {
          const val = row[col.key]
          if (col.format === 'currency' || col.format === 'number') {
            return `<Cell ss:StyleID="${col.format}"><Data ss:Type="Number">${Number(val ?? 0)}</Data></Cell>`
          }
          return `<Cell ss:StyleID="data"><Data ss:Type="String">${escapeXml(val)}</Data></Cell>`
        })
        .join('')
      currentRow++
      return `<Row>${cells}</Row>`
    })
    .join('\n')

  // Totals row
  let totalsRowXml = ''
  if (totalsRow) {
    const cells = columns
      .map((col) => {
        const val = totalsRow[col.key]
        if (col.format === 'currency' || col.format === 'number') {
          return `<Cell ss:StyleID="total_num"><Data ss:Type="Number">${Number(val ?? 0)}</Data></Cell>`
        }
        return `<Cell ss:StyleID="total"><Data ss:Type="String">${escapeXml(val ?? '')}</Data></Cell>`
      })
      .join('')
    totalsRowXml = `<Row ss:Height="20">${cells}</Row>`
  }

  // Freeze pane
  const freezePane = freezeHeaderRow
    ? `
    <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
      <FreezePanes/>
      <FrozenNoSplit/>
      <SplitHorizontal>${headerRowIdx}</SplitHorizontal>
      <TopRowBottomPane>${headerRowIdx}</TopRowBottomPane>
    </WorksheetOptions>`
    : ''

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:x="urn:schemas-microsoft-com:office:excel">
  <Styles>
    <Style ss:ID="Default">
      <Font ss:FontName="Calibri" ss:Size="10"/>
    </Style>
    <Style ss:ID="title">
      <Font ss:FontName="Calibri" ss:Size="14" ss:Bold="1"/>
      <Alignment ss:Horizontal="Center"/>
    </Style>
    <Style ss:ID="subtitle">
      <Font ss:FontName="Calibri" ss:Size="11"/>
      <Alignment ss:Horizontal="Center"/>
    </Style>
    <Style ss:ID="header">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1" ss:Color="#FFFFFF"/>
      <Interior ss:Color="#4F46E5" ss:Pattern="Solid"/>
      <Alignment ss:Horizontal="Center"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
      </Borders>
    </Style>
    <Style ss:ID="data">
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CCCCCC"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CCCCCC"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CCCCCC"/>
      </Borders>
    </Style>
    <Style ss:ID="currency">
      <NumberFormat ss:Format="₹#,##0.00"/>
      <Alignment ss:Horizontal="Right"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CCCCCC"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CCCCCC"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CCCCCC"/>
      </Borders>
    </Style>
    <Style ss:ID="number">
      <Alignment ss:Horizontal="Center"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CCCCCC"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CCCCCC"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#CCCCCC"/>
      </Borders>
    </Style>
    <Style ss:ID="total">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1"/>
      <Interior ss:Color="#F3F4F6" ss:Pattern="Solid"/>
      <Borders>
        <Border ss:Position="Top" ss:LineStyle="Double" ss:Weight="3"/>
        <Border ss:Position="Bottom" ss:LineStyle="Double" ss:Weight="3"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
      </Borders>
    </Style>
    <Style ss:ID="total_num">
      <Font ss:FontName="Calibri" ss:Size="10" ss:Bold="1"/>
      <NumberFormat ss:Format="₹#,##0.00"/>
      <Alignment ss:Horizontal="Right"/>
      <Interior ss:Color="#F3F4F6" ss:Pattern="Solid"/>
      <Borders>
        <Border ss:Position="Top" ss:LineStyle="Double" ss:Weight="3"/>
        <Border ss:Position="Bottom" ss:LineStyle="Double" ss:Weight="3"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
      </Borders>
    </Style>
  </Styles>
  <Worksheet ss:Name="${escapeXml(sheetName)}">
    <Table ss:DefaultColumnWidth="80" ss:ExpandedColumnCount="${columns.length}" ss:ExpandedRowCount="${currentRow + (totalsRow ? 1 : 0)}">
      ${colDefs}
      ${titleRow}
      ${subtitleRow}
      ${emptyRow}
      ${headerRow}
      ${dataRows}
      ${totalsRowXml}
    </Table>
    ${freezePane}
  </Worksheet>
</Workbook>`

  const blob = new Blob([xml], { type: 'application/vnd.ms-excel' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  // Support both .xls and .xlsx extensions
  const hasExtension = fileName.endsWith('.xls') || fileName.endsWith('.xlsx')
  link.download = hasExtension ? fileName : `${fileName}.xlsx`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
